import { z } from "zod";
import {
  assetKindSchema,
  decimalByteCountSchema,
  referenceRoleSchema,
  seedanceCapabilityCatalogSchema,
  seedanceModelSchema,
  seedanceRatioSchema,
  seedanceResolutionSchema
} from "./seedance.js";

export { referenceRoleSchema } from "./seedance.js";

export const maxCanvasDocumentBytes = 10 * 1024 * 1024;
const timestampSchema = z.iso.datetime();
export const pointSchema = z.strictObject({
  x: z.number().finite(),
  y: z.number().finite()
});
const sizeSchema = z.strictObject({
  width: z.number().finite().min(120).max(2400),
  height: z.number().finite().min(80).max(2400)
});
const baseNodeSchema = z.strictObject({
  id: z.uuid(),
  position: pointSchema,
  size: sizeSchema,
  collapsed: z.boolean()
});

export const referenceModeSchema = z.enum([
  "strict",
  "action_only",
  "camera_only",
  "rhythm_only",
  "style_only",
  "custom"
]);

export const seedanceNodeSettingsSchema = z.strictObject({
  prompt: z.string().max(20_000),
  model: seedanceModelSchema,
  ratio: seedanceRatioSchema,
  durationSeconds: z.number().int().positive(),
  resolution: seedanceResolutionSchema,
  watermark: z.boolean(),
  generateAudio: z.boolean(),
  seed: z.number().int()
});

const imageNodeSchema = baseNodeSchema.extend({
  kind: z.literal("image"),
  assetId: z.uuid()
});
const videoNodeSchema = baseNodeSchema.extend({
  kind: z.literal("video"),
  assetId: z.uuid()
});
const audioNodeSchema = baseNodeSchema.extend({
  kind: z.literal("audio"),
  assetId: z.uuid()
});

export const scriptCanvasNodeSchema = baseNodeSchema.extend({
  kind: z.literal("script"),
  title: z.string().min(1).max(160),
  synopsis: z.string().max(20_000)
});

const canvasNodeV1Schema = z.discriminatedUnion("kind", [
  baseNodeSchema.extend({ kind: z.literal("text"), text: z.string().max(50_000) }),
  imageNodeSchema,
  videoNodeSchema,
  audioNodeSchema,
  baseNodeSchema.extend({
    kind: z.literal("seedance"),
    settings: seedanceNodeSettingsSchema
  })
]);

export const canvasNodeSchema = z.discriminatedUnion("kind", [
  baseNodeSchema.extend({ kind: z.literal("text"), text: z.string().max(50_000) }),
  imageNodeSchema,
  videoNodeSchema,
  audioNodeSchema,
  scriptCanvasNodeSchema,
  baseNodeSchema.extend({
    kind: z.literal("seedance"),
    settings: seedanceNodeSettingsSchema
  })
]);

export const canvasEdgeSchema = z.strictObject({
  id: z.uuid(),
  sourceNodeId: z.uuid(),
  targetNodeId: z.uuid(),
  role: referenceRoleSchema,
  mode: referenceModeSchema.optional(),
  instruction: z.string().max(2_000),
  order: z.number().int().nonnegative()
});

export const canvasGroupSchema = z.strictObject({
  id: z.uuid(),
  label: z.string().max(120),
  nodeIds: z.array(z.uuid()),
  color: z.string().max(32)
});

function createCanvasDocumentSchema<
  const TVersion extends 1 | 2,
  TNode extends z.ZodType
>(
  schemaVersion: TVersion,
  nodeSchema: TNode
) {
  return z
  .strictObject({
    schemaVersion: z.literal(schemaVersion),
    viewport: pointSchema.extend({ zoom: z.number().finite().min(0.05).max(4) }),
    nodes: z.array(nodeSchema).max(5_000),
    edges: z.array(canvasEdgeSchema).max(10_000),
    groups: z.array(canvasGroupSchema).max(1_000)
  })
  .superRefine((document, context) => {
    const collections: Array<[string, Array<{ id: string }>]> = [
      ["nodes", document.nodes as Array<{ id: string }>],
      ["edges", document.edges]
    ];
    for (const [collectionName, values] of collections) {
      const seenIds = new Set<string>();
      values.forEach(({ id }, index) => {
        if (seenIds.has(id)) {
          context.addIssue({
            code: "custom",
            path: [collectionName, index, "id"],
            message: `画布${collectionName === "nodes" ? "节点" : "连线"} ID 必须唯一。`
          });
        } else {
          seenIds.add(id);
        }
      });
    }
    if (new TextEncoder().encode(JSON.stringify(document)).byteLength > maxCanvasDocumentBytes) {
      context.addIssue({
        code: "custom",
        message: "画布文档的未压缩 JSON 不得超过 10 MiB。"
      });
    }
  });
}

const canvasDocumentV1Schema = createCanvasDocumentSchema(1, canvasNodeV1Schema);
export const canvasDocumentSchema = createCanvasDocumentSchema(2, canvasNodeSchema);

export function upgradeCanvasDocument(document: unknown): CanvasDocument {
  const current = canvasDocumentSchema.safeParse(document);
  if (current.success) {
    return current.data;
  }
  const v1 = canvasDocumentV1Schema.parse(document);
  return canvasDocumentSchema.parse({ ...v1, schemaVersion: 2 });
}

export const saveCanvasRequestSchema = z.strictObject({
  revision: decimalByteCountSchema,
  document: canvasDocumentSchema
});

export const assetVisibilitySchema = z.enum(["private", "team"]);
export const assetOriginSchema = z.enum(["manual", "seedance_generated"]);
export const assetListViewSchema = z.enum([
  "library",
  "team",
  "history",
  "trash"
]);

export const assetViewSchema = z.strictObject({
  id: z.uuid(),
  displayName: z.string().min(1).max(255),
  kind: assetKindSchema,
  mimeType: z.string().min(1).max(255),
  sizeBytes: decimalByteCountSchema,
  visibility: assetVisibilitySchema.default("private"),
  origin: assetOriginSchema.default("manual"),
  teamUnpublishedAt: timestampSchema.nullable().default(null),
  ownerDisplayName: z.string().min(1).max(255).optional(),
  canManage: z.boolean().default(false),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  durationSeconds: z.number().finite().nonnegative().optional(),
  lastUsedAt: timestampSchema,
  trashedAt: timestampSchema.nullable(),
  purgeAfter: timestampSchema.nullable(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
});

export const assetListQuerySchema = z.strictObject({
  view: assetListViewSchema,
  kind: assetKindSchema.optional(),
  q: z.string().trim().min(1).max(255).optional(),
  cursor: z.string().min(1).max(2_048).optional(),
  limit: z.coerce.number().int().positive().max(100).default(50)
});

export const assetListResponseSchema = z.strictObject({
  assets: z.array(assetViewSchema),
  nextCursor: z.string().min(1).max(2_048).optional()
});

export const legacyPromptTemplateVisibilitySchema = assetVisibilitySchema;
export const legacyPromptTemplateCreateSchema = z.strictObject({
  name: z.string().trim().min(1).max(160),
  body: z.string().trim().min(1).max(20_000),
  visibility: legacyPromptTemplateVisibilitySchema
});
export const legacyPromptTemplateUpdateSchema = legacyPromptTemplateCreateSchema.partial().refine(
  (value) => Object.keys(value).length > 0,
  "至少需要更新一个模板字段。"
);
export const legacyPromptTemplateViewSchema = z.strictObject({
  id: z.uuid(),
  name: z.string().min(1).max(160),
  body: z.string().min(1).max(20_000),
  visibility: legacyPromptTemplateVisibilitySchema,
  teamUnpublishedAt: timestampSchema.nullable().default(null),
  ownerDisplayName: z.string().min(1).max(255),
  canManage: z.boolean(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema
});
export const legacyPromptTemplateListQuerySchema = z.strictObject({
  view: z.enum(["private", "team"]),
  q: z.string().trim().min(1).max(255).optional(),
  cursor: z.string().min(1).max(2_048).optional(),
  limit: z.coerce.number().int().positive().max(100).default(50)
});
export const legacyPromptTemplateListResponseSchema = z.strictObject({
  templates: z.array(legacyPromptTemplateViewSchema),
  nextCursor: z.string().min(1).max(2_048).optional()
});

export const canvasViewSchema = z.strictObject({
  canvasId: z.uuid(),
  projectId: z.uuid(),
  revision: decimalByteCountSchema,
  document: canvasDocumentSchema,
  updatedAt: timestampSchema
});

export const workflowInsertionViewSchema = z.strictObject({
  canvas: canvasViewSchema,
  inserted: z.strictObject({
    nodeIds: z.array(z.uuid()).max(5_000),
    edgeIds: z.array(z.uuid()).max(10_000),
    groupIds: z.array(z.uuid()).max(1_000)
  })
});

export const seedanceCapabilitiesViewSchema = seedanceCapabilityCatalogSchema;

const seedanceReferencePreviewSchema = z.strictObject({
  assetId: z.uuid(),
  nodeId: z.uuid(),
  edgeId: z.uuid(),
  kind: assetKindSchema,
  mention: z.string().regex(/^@(图片|视频|音频)[1-9]\d*$/u),
  role: referenceRoleSchema,
  mode: referenceModeSchema.optional(),
  instruction: z.string().max(2_000),
  order: z.number().int().nonnegative()
});
const previewMessageSchema = z.strictObject({
  code: z.string().min(1).max(120),
  message: z.string().min(1).max(2_000)
});

export const seedanceRequestPreviewSchema = z
  .strictObject({
    ready: z.boolean(),
    finalPrompt: z.string().max(20_000),
    references: z.array(seedanceReferencePreviewSchema).max(100),
    parameters: seedanceNodeSettingsSchema,
    errors: z.array(previewMessageSchema),
    warnings: z.array(previewMessageSchema)
  });

export type ReferenceMode = z.infer<typeof referenceModeSchema>;
export type SeedanceNodeSettings = z.infer<typeof seedanceNodeSettingsSchema>;
export type CanvasDocument = z.infer<typeof canvasDocumentSchema>;
export type CanvasNode = z.infer<typeof canvasNodeSchema>;
export type CanvasEdge = z.infer<typeof canvasEdgeSchema>;
export type CanvasGroup = z.infer<typeof canvasGroupSchema>;
export type Point = z.infer<typeof pointSchema>;
export type AssetView = z.infer<typeof assetViewSchema>;
export type AssetListQuery = z.infer<typeof assetListQuerySchema>;
export type AssetListResponse = z.infer<typeof assetListResponseSchema>;
export type AssetVisibility = z.infer<typeof assetVisibilitySchema>;
export type AssetOrigin = z.infer<typeof assetOriginSchema>;
export type AssetListView = z.infer<typeof assetListViewSchema>;
export type LegacyPromptTemplateCreate = z.infer<typeof legacyPromptTemplateCreateSchema>;
export type LegacyPromptTemplateUpdate = z.infer<typeof legacyPromptTemplateUpdateSchema>;
export type LegacyPromptTemplateView = z.infer<typeof legacyPromptTemplateViewSchema>;
export type LegacyPromptTemplateListQuery = z.infer<typeof legacyPromptTemplateListQuerySchema>;
export type LegacyPromptTemplateListResponse = z.infer<typeof legacyPromptTemplateListResponseSchema>;
export type CanvasView = z.infer<typeof canvasViewSchema>;
export type WorkflowInsertionView = z.infer<typeof workflowInsertionViewSchema>;
export type SaveCanvasRequest = z.infer<typeof saveCanvasRequestSchema>;
export type SeedanceCapabilitiesView = z.infer<typeof seedanceCapabilitiesViewSchema>;
export type SeedanceRequestPreview = z.infer<typeof seedanceRequestPreviewSchema>;

export function canvasCollectionIdError(input: Pick<
  CanvasDocument,
  "nodes" | "edges" | "groups"
>): string | undefined {
  const collections: Array<[string, ReadonlyArray<{ id: string }>]> = [
    ["节点", input.nodes],
    ["连线", input.edges],
    ["分组", input.groups]
  ];
  for (const [name, values] of collections) {
    const ids = new Set<string>();
    for (const { id } of values) {
      if (ids.has(id)) return `画布${name} ID 必须唯一。`;
      ids.add(id);
    }
  }
  return undefined;
}
